import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { themeSchema, type Theme } from '@xfile/shared';
import { applyTheme, detectInitialTheme, storeTheme, subscribeSystemTheme } from './theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** Options ordonnées pour le menu (clair / sombre / système). */
  themes: readonly Theme[];
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const AVAILABLE_THEMES = themeSchema.options;

interface ThemeProviderProps {
  children: React.ReactNode;
  /** Thème initial forcé (tests) ; sinon détecté (localStorage → system). */
  initialTheme?: Theme;
}

export function ThemeProvider({ children, initialTheme }: ThemeProviderProps): React.JSX.Element {
  const [theme, setThemeState] = useState<Theme>(() => initialTheme ?? detectInitialTheme());

  // Applique la classe `dark` à chaque changement de préférence.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Réagit aux changements système uniquement quand la préférence = `system`.
  useEffect(() => {
    if (theme !== 'system') return;
    return subscribeSystemTheme(() => applyTheme('system'));
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    storeTheme(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, themes: AVAILABLE_THEMES }),
    [theme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Accès au thème courant, setter et options. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme doit être utilisé dans un <ThemeProvider>');
  return ctx;
}
